import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  CreditCard, 
  Download, 
  Filter,
  Search,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Info,
  Eye,
  MessageSquare,
  RefreshCw,
  BarChart3,
  Building2,
  Edit,
  FileText,
  X,
  Loader2,
  Home,
  Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TransactionPropertySuggestion from '@/components/banking/TransactionPropertySuggestion';
import { capitalFetch } from '@/services/capitalApiService.js';

/**
 * BankingPage — Capital Atlas Bridge (BANKING-RECONCILIATION-CAPITAL-BRIDGE-1)
 * 
 * Datenquelle: Capital Atlas tRPC via /api/capital/trpc/
 *   - bankAccounts.knownAccounts  → Kontenübersicht
 *   - bankAccounts.stagingTransactions → Transaktionen
 *   - bankAccounts.summary → Badge-Counts
 * 
 * KEIN FinAPI. Bankdaten kommen aus CAMT-XML-Importen via Vault.
 * Privatkonto Torsten Hiss (DE89670900000010260701) ist bewusst importiert
 * für steuerliche Zwecke — in der UI als "Privat" gekennzeichnet.
 */

// ─── Capital Atlas Helper ──────────────────────────────────────────────────
const CAPITAL_BASE = '/api/capital/trpc';

async function capitalGet(procedure, input = null) {
  const url = input
    ? `${CAPITAL_BASE}/${procedure}?input=${encodeURIComponent(JSON.stringify({ json: input }))}`
    : `${CAPITAL_BASE}/${procedure}`;
  const res = await capitalFetch(url);
  if (!res.ok) throw new Error(`Capital API ${procedure}: HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.json?.message ?? 'Capital API Fehler');
  return data.result.data.json;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────
const formatCurrency = (value) => {
  const numValue = Number(value) || 0;
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numValue);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE');
};

const isPrivateAccount = (account) => {
  return account?.iban === 'DE89670900000010260701' || 
         account?.property_name?.toLowerCase().includes('privat');
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────
const BankingPage = () => {
  const navigate = useNavigate();

  // Daten-State
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastLoaded, setLastLoaded] = useState(null);

  // Filter-State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('90');

  // Modal-State
  const [showTransactionDetailModal, setShowTransactionDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionSuggestionModal, setShowTransactionSuggestionModal] = useState(false);

  // ─── Daten laden ────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Konten laden
      const accountsData = await capitalGet('bankAccounts.knownAccounts');
      setAccounts(accountsData || []);

      // Summary laden
      const summaryData = await capitalGet('bankAccounts.summary');
      setSummary(summaryData);

      // Transaktionen laden — gefiltert nach ausgewähltem Konto und Zeitraum
      const txInput = {
        limit: 200,
        ...(selectedAccount !== 'all' && { propertyName: selectedAccount }),
        ...(selectedDateRange !== 'all' && { daysBack: parseInt(selectedDateRange) }),
      };
      const txData = await capitalGet('bankAccounts.getTransactions', txInput);
      setTransactions(txData?.transactions || []);
      setLastLoaded(new Date());
    } catch (err) {
      console.error('Capital Atlas Banking Fehler:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccount, selectedDateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Gefilterte Transaktionen ────────────────────────────────────────────
  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = !searchTerm || 
      tx.reference_raw?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.counterpart_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.property_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // ─── Kennzahlen aus Transaktionen ────────────────────────────────────────
  const totalIncome = filteredTransactions
    .filter(tx => Number(tx.amount) > 0)
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const totalExpenses = filteredTransactions
    .filter(tx => Number(tx.amount) < 0)
    .reduce((sum, tx) => sum + Math.abs(Number(tx.amount)), 0);

  const netCashFlow = totalIncome - totalExpenses;

  // ─── Loading State ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400">Lade Bankdaten aus Capital Atlas...</p>
        </div>
      </div>
    );
  }

  // ─── Error State ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <AlertTriangle className="w-5 h-5" />
              Capital Atlas nicht erreichbar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700 dark:text-red-300 text-sm mb-4">{error}</p>
            <Button onClick={loadData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Banking & Transaktionen
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
            <Database className="w-3 h-3" />
            Capital Atlas · {transactions.length} Transaktionen
            {lastLoaded && ` · Stand: ${lastLoaded.toLocaleTimeString('de-DE')}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Aktualisieren
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <Home className="w-4 h-4 mr-1" />
            Zurück
          </Button>
        </div>
      </div>

      {/* Datenquelle-Badge */}
      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
        <span className="text-sm text-green-800 dark:text-green-200">
          <strong>Datenquelle: Capital Atlas</strong> — {accounts.length} Konten · {transactions.length} Transaktionen aus CAMT-Import
          {summary?.pendingAssignments > 0 && (
            <span className="ml-2 text-amber-700 dark:text-amber-300">
              · {summary.pendingAssignments} Buchungen ohne Objekt-Zuordnung
            </span>
          )}
        </span>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className={`w-5 h-5 ${netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              Netto Cash Flow
            </CardTitle>
            <CardDescription>Gefilterter Zeitraum</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {netCashFlow >= 0 ? '+' : '-'}€{formatCurrency(Math.abs(netCashFlow))}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredTransactions.length} Transaktionen
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Ausgaben
            </CardTitle>
            <CardDescription>Gefilterter Zeitraum</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              €{formatCurrency(totalExpenses)}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredTransactions.filter(tx => Number(tx.amount) < 0).length} Buchungen
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Einnahmen
            </CardTitle>
            <CardDescription>Gefilterter Zeitraum</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              €{formatCurrency(totalIncome)}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredTransactions.filter(tx => Number(tx.amount) > 0).length} Buchungen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Kontenübersicht */}
      {accounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Konten ({accounts.length})
            </CardTitle>
            <CardDescription>Aus CAMT-Import · Capital Atlas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((account, idx) => (
                <div
                  key={idx}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedAccount === account.property_name
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedAccount(
                    selectedAccount === account.property_name ? 'all' : account.property_name
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
                      {account.property_name || account.iban}
                    </h3>
                    {isPrivateAccount(account) && (
                      <Badge variant="outline" className="text-xs ml-1 flex-shrink-0">Privat</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mb-2">
                    {account.iban}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {account.tx_count} Buchungen
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      bis {formatDate(account.newest)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaktionen */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Transaktionen
              </CardTitle>
              <CardDescription>
                {selectedAccount !== 'all' ? `Konto: ${selectedAccount}` : 'Alle Konten'}
                {' · '}{filteredTransactions.length} Einträge
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTransactionSuggestionModal(true)}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Objekt-Zuordnung
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter-Zeile */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Suche nach Verwendungszweck, Gegenseite..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedDateRange} onValueChange={setSelectedDateRange}>
              <SelectTrigger className="w-40">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 Tage</SelectItem>
                <SelectItem value="90">90 Tage</SelectItem>
                <SelectItem value="180">6 Monate</SelectItem>
                <SelectItem value="365">1 Jahr</SelectItem>
                <SelectItem value="all">Alle</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Transaktionsliste */}
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Keine Transaktionen gefunden</p>
              {searchTerm && (
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSearchTerm('')}>
                  Filter zurücksetzen
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredTransactions.map((tx, idx) => {
                // VAULT-CAMT-ENRICHMENT-1
                // is_self_account_reference kommt direkt aus Capital Atlas DB (backfilled)
                // Kein Inline-Vergleich mehr nötig — Quelle ist counterparty_source
                const isOwnAccount = tx.is_self_account_reference === true;
                const displayTitle = isOwnAccount
                  ? (tx.reference_raw || tx.classification_label || tx.counterpart_name || 'Unbekannte Buchung')
                  : (tx.counterpart_name || tx.reference_raw || tx.classification_label || 'Unbekannte Buchung');
                const displaySub = isOwnAccount
                  ? tx.counterpart_name
                  : (tx.reference_raw || tx.purpose || '—');
                return (
                  <div
                    key={tx.id || idx}
                    className="flex items-center justify-between p-3 border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedTransaction(tx);
                      setShowTransactionDetailModal(true);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {displayTitle}
                        </span>
                        {tx.property_name && (
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            {tx.property_name}
                          </Badge>
                        )}
                        {isOwnAccount && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0" title="Gegenseite nicht im CAMT — Referenztext wird angezeigt">Ref.</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {displaySub || '—'}
                      </p>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      <div className={`font-bold text-sm ${Number(tx.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(tx.amount) >= 0 ? '+' : ''}€{formatCurrency(tx.amount)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatDate(tx.booking_date || tx.bankBookingDate)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Modal */}
      <Dialog open={showTransactionDetailModal} onOpenChange={setShowTransactionDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Transaktionsdetails</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Betrag</label>
                  <p className={`text-lg font-bold ${
                    Number(selectedTransaction.amount) > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {Number(selectedTransaction.amount) > 0 ? '+' : ''}€{formatCurrency(Math.abs(Number(selectedTransaction.amount)))}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Datum</label>
                  <p className="text-gray-900 dark:text-white">
                    {formatDate(selectedTransaction.booking_date || selectedTransaction.bankBookingDate)}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Empfänger/Absender</label>
                {(() => {
                  const isOwn = selectedTransaction.is_self_account_reference === true;
                  return (
                    <>
                      <p className="text-gray-900 dark:text-white">
                        {isOwn
                          ? (selectedTransaction.reference_raw || selectedTransaction.classification_label || selectedTransaction.counterpart_name || 'Unbekannte Buchung')
                          : (selectedTransaction.counterpart_name || selectedTransaction.reference_raw || 'Unbekannt')}
                      </p>
                      {isOwn && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          Kontoinhaber: {selectedTransaction.counterpart_name} · Referenz verwendet
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Verwendungszweck</label>
                <p className="text-gray-900 dark:text-white">
                  {selectedTransaction.reference_raw || selectedTransaction.purpose || 'Kein Verwendungszweck'}
                </p>
              </div>
              {selectedTransaction.property_name && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Objekt</label>
                  <p className="text-gray-900 dark:text-white">{selectedTransaction.property_name}</p>
                </div>
              )}
              {selectedTransaction.iban && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">IBAN</label>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{selectedTransaction.iban}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTransactionDetailModal(false);
                    setShowTransactionSuggestionModal(true);
                  }}
                  className="flex-1"
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  Objekt zuordnen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Property Suggestion Modal */}
      {showTransactionSuggestionModal && (
        <TransactionPropertySuggestion
          isOpen={showTransactionSuggestionModal}
          onClose={() => setShowTransactionSuggestionModal(false)}
          transactions={filteredTransactions}
        />
      )}
    </div>
  );
};

export default BankingPage;
